/**
 * One-shot device probe used to pick sensible render resolution defaults.
 * GPU name via WEBGL_debug_renderer_info on a throwaway context (released straight
 * away), plus core count / touch / screen heuristics. Cached for the page lifetime.
 * Runtime safety net: the multiview's adaptive program scale handles anything we misjudge.
 */
export type DeviceTier = 'low' | 'mid' | 'high';

export interface DeviceProfile {
  tier: DeviceTier;
  /** Highest canvas pixel ratio worth paying for on this device. */
  maxPixelRatio: number;
  gpu: string;
}

let cached: DeviceProfile | null = null;

const WEAK_GPU = /swiftshader|llvmpipe|software|mali|adreno \(tm\) [1-5]\d\d|adreno [1-5]\d\d|powervr|intel\(r\) (hd|uhd)|intel hd|intel uhd|gma/i;
const STRONG_GPU = /apple m\d|apple gpu|nvidia|geforce|rtx|radeon rx|radeon pro|arc a\d/i;

export function deviceProfile(): DeviceProfile {
  if (cached) return cached;
  let gpu = '';
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') ?? c.getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      gpu = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  } catch { /* no probe → heuristics only */ }
  const cores = navigator.hardwareConcurrency || 4;
  const mobile = /android|iphone|ipad|mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && Math.min(screen.width, screen.height) < 900);
  let tier: DeviceTier = 'mid';
  if (WEAK_GPU.test(gpu) || cores <= 2) tier = 'low';
  else if (STRONG_GPU.test(gpu) && cores >= 8 && !mobile) tier = 'high';
  else if (mobile || cores <= 4) tier = 'low';
  cached = { tier, gpu, maxPixelRatio: tier === 'high' ? 1.5 : tier === 'mid' ? 1.25 : 1 };
  return cached;
}
